import { Poi } from '@/models/poi';
import { Route } from '@/models/route';
import { PoiState } from '@/models/poi';

const WHITE = '#ffffff';

// locked (light blue)
const LIGHT_BLUE = '#0078d0';
const LIGHT_BLUE_FILL = '#a7c0d1';

// ready to unlock (orange)
const ORANGE = '#ffa200';
const ORANGE_FILL = '#ffd68f';

// unlocked (liechtenstein blue)
const BLUE = '#175da8';
const BLUE_FILL = '#adc2d9';

const SELECTABLE_POI_RADIUS = 5;
const OVERVIEW_POI_RADIUS = 3;

const POI_LOCKED_RADIUS_STOPS = [[9, 12], [13, 12], [16, 60], [18, 244]];

const LOCKED = PoiState.LOCKED;
const R2U = PoiState.READY_TO_UNLOCK;
const UNLOCKED = PoiState.UNLOCKED;

export default class MapPoiHelper {
    public static findFirstLabelLayerId(map: mapboxgl.Map): string {
        const layers = map.getStyle().layers;

        // Find the index of the first symbol label layer in the map style
        for (const actualLayer of layers) {
            if (actualLayer.type === 'symbol' && actualLayer.id.includes('label')) {
                return actualLayer.id;
            }
        }

        return '';
    }

    public static addRoute(map: mapboxgl.Map, route: Route, beforeId?: string) {
        map.addLayer(
            {
                id: 'route-' + route.id,
                type: 'line',
                source: {
                    type: 'geojson',
                    data: route.geojson_line
                },
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': BLUE,
                    'line-width': SELECTABLE_POI_RADIUS
                }
            },
            beforeId
        );
    }

    public static removeRoute(map: mapboxgl.Map, route: Route) {
        map.removeLayer('route-' + route.id);
    }

    public static addPois(map: mapboxgl.Map, source: string) {
        // *** REGION unselected ***
        // Unselected Halos Blau
        map.addLayer({
            id: 'poiHalos',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': LIGHT_BLUE,
                'circle-radius': { base: 2, stops: POI_LOCKED_RADIUS_STOPS },
                'circle-opacity': 0.25,
                'circle-blur': 0.25,
                'circle-pitch-alignment': 'map',
                'circle-stroke-width': 0.5,
                'circle-stroke-color': BLUE,
                'circle-stroke-opacity': 0.55,
            },
            filter: ['all', ['!', ['get', 'selected']], ['==', ['get', 'state'], LOCKED]]
        });

        // Unselected POI dot Blau
        map.addLayer({
            id: 'poiDots',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': ['match', ['get', 'state'], LOCKED, LIGHT_BLUE_FILL, BLUE_FILL],
                'circle-radius': SELECTABLE_POI_RADIUS,
                'circle-stroke-width': SELECTABLE_POI_RADIUS,
                'circle-stroke-color': ['match', ['get', 'state'], LOCKED, LIGHT_BLUE, BLUE],
                'circle-pitch-alignment': 'map'
            },
            filter: ['all', ['!', ['get', 'selected']], ['!=', ['get', 'state'], R2U]]
        });

        // Unselected Halos Orange
        map.addLayer({
            id: 'poiHalosOrange',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': ORANGE,
                'circle-radius': { base: 2, stops: POI_LOCKED_RADIUS_STOPS },
                'circle-opacity': 0.5,
                'circle-blur': 1,
                'circle-pitch-alignment': 'map'
            },
            filter: ['all', ['!', ['get', 'selected']], ['==', ['get', 'state'], R2U]]
        });

        // Unselected POI dot Orange
        map.addLayer({
            id: 'poiDotsOrange',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': ORANGE_FILL,
                'circle-radius': SELECTABLE_POI_RADIUS,
                'circle-stroke-width': SELECTABLE_POI_RADIUS,
                'circle-stroke-color': ORANGE,
                'circle-pitch-alignment': 'map'
            },
            filter: ['all', ['!', ['get', 'selected']], ['==', ['get', 'state'], R2U]]
        });

        // *** REGION SELECTED ***
        // Selected Halo (Locked Pois)
        map.addLayer({
            id: 'poiHalosSelected',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': ['match', ['get', 'state'], R2U, ORANGE, LIGHT_BLUE],
                'circle-radius': { base: 2, stops: POI_LOCKED_RADIUS_STOPS },
                'circle-opacity': ['match', ['get', 'state'], LOCKED, 0.25, R2U, 0.5, 0],
                'circle-blur': ['match', ['get', 'state'], R2U, 1, 0],
                'circle-stroke-width': ['match', ['get', 'state'], LOCKED, 1.5, 0],
                'circle-stroke-color': LIGHT_BLUE,
                'circle-pitch-alignment': 'map'
            },
            filter: ['get', 'selected']
        });

        // Selected POI Dot
        map.addLayer({
            id: 'poiDotsSelected',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': WHITE,
                'circle-radius': SELECTABLE_POI_RADIUS,
                'circle-stroke-width': SELECTABLE_POI_RADIUS,
                'circle-stroke-color': ['match', ['get', 'state'], R2U, ORANGE, UNLOCKED, BLUE, LIGHT_BLUE],
                'circle-pitch-alignment': 'map'
            },
            filter: ['get', 'selected']
        });

        // Selected Outline (Ready to Unlock, Unlocked)
        map.addLayer({
            id: 'poiOutlinesSelected',
            type: 'circle',
            source: source,
            paint: {
                'circle-radius': 12,
                'circle-opacity': 0,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': ['match', ['get', 'state'], R2U, ORANGE, UNLOCKED, BLUE, LIGHT_BLUE],
                'circle-stroke-opacity': 1,
                'circle-pitch-alignment': 'map'
            },
            filter: ['all', ['get', 'selected'], ['!=', ['get', 'state'], LOCKED]]
        });
    }

    public static addRouteOverviewPois(map: mapboxgl.Map, source: string) {
        map.addLayer({
            id: 'poiOverview',
            type: 'circle',
            source: source,
            paint: {
                'circle-color': ['case', ['==', ['get', 'selected'], true], '#c87782', '#b2b2b2'],
                'circle-radius': OVERVIEW_POI_RADIUS,
                'circle-stroke-color': ['case', ['==', ['get', 'selected'], true], '#C80720', '#888888'],
                'circle-stroke-width': OVERVIEW_POI_RADIUS,
                'circle-pitch-alignment': 'map'
            }
        });
    }

    public static createPoisGeoJSON(pois: Poi[]): Array<GeoJSON.Feature> {
        const geoJsonFeatures = new Array<GeoJSON.Feature>();

        for (const poi of pois) {
            geoJsonFeatures.push({
                id: poi.id,
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [poi.coordinates.longitude, poi.coordinates.latitude]
                },
                properties: {
                    selected: false,
                    state: poi.state
                }
            });
        }

        return geoJsonFeatures;
    }

    public static selectPois(features: GeoJSON.Feature[], pois: Poi[]) {
        for (const feature of features) {
            const isSelected = pois.some(poi => poi.id === feature.id);
            feature.properties.selected = isSelected;
        }
    }

    /*private static createPolyCircle(map: Map, position: Position, radiusKm: number = 0.5, pointCount: number = 32) {
        var km = radiusKm;

        var ret = [];
        var distanceX = km / (111.320 * Math.cos(position.coords.latitude * Math.PI / 180));
        var distanceY = km / 110.574;

        var theta, x, y;
        for (var i = 0; i < pointCount; i++) {
            theta = (i / pointCount) * (2 * Math.PI);
            x = distanceX * Math.cos(theta);
            y = distanceY * Math.sin(theta);

            ret.push([position.coords.longitude + x, position.coords.latitude + y]);
        }
        ret.push(ret[0]);

        map.addSource("polygon", {
            "type": "geojson",
            "data": {
                "type": "FeatureCollection",
                "features": [{
                    "type": "Feature",
                    "geometry": {
                        "type": "Polygon",
                        "coordinates": [ret]
                    }
                }]
            }
        });

        map.addLayer({
            "id": "polygon",
            "type": "fill",
            "source": "polygon",
            "layout": {},
            "paint": {
                "fill-color": "blue",
                "fill-opacity": 0.2
            }
        });
    }*/
}
